import {defineStore} from "pinia";
import {api, cookieName} from '@/api/client';
import type {UserInterface} from "@/types/auth";
import {handleError} from "@/appdata/globals.ts";
import { useAppDataStore } from "@/stores/app-data";

export const useAuthDataStore = defineStore('auth', {
    state: () => ({
        user: null as UserInterface | null,
        authenticated: false as boolean,
        initialized: false as boolean,
    }),
    getters: {
        userEnabled(state): boolean {
            // console.debug(state.user);
            return state.authenticated;
        },
        canSelectWorld(state): boolean {
            return !!(state.user?.subscriber || state.user?.supporter || (state.user?.sharedWorldCount ?? 0) > 0);
        },
        canCreateWorld(state): boolean {
            return !!(state.user?.subscriber || state.user?.supporter);
        },
        canCreateElement(state): boolean {
            return !!(state.user?.subscriber || state.user?.supporter);
        },
        // Premium library worlds are for users with a plan (the API's WorldVoter::DETAILS).
        hasPremiumAccess(state): boolean {
            return !!(state.user?.subscriber || state.user?.supporter);
        },
        isAdministrator(state): boolean {
            return state.user?.administrator === true;
        },
        // Whether the account was created at least 30 days ago. Thirty days of elapsed time, not
        // calendar days, so a daylight-saving change in between doesn't shift it by an hour.
        agedAtLeastThirtyDays(state): boolean {
            const created = new Date(state.user?.created ?? NaN).getTime();
            if (Number.isNaN(created)) return false;

            return Date.now() - created >= 30 * 24 * 60 * 60 * 1000;
        },
    },
    actions: {
        async requireAuth() {
            //TODO: Push to login form
            await this.authenticate()
        },
        async authenticate() {
            let json = null;
            const response = await api.get('/auth/user')
                .then((response) => {
                    if (response.data.authenticated)
                    {
                        this.user = response.data;
                    }
                    this.authenticated = response.data.authenticated;
                    json = response;
                })
                .catch((error) => { handleError(error); })

            return json;
        },
        async storeProfile() {
            return api.patch('/profile', this.user)
                .then((response) => {
                    this.user = response.data;
                })
                .catch((error) => { handleError(error); })
        },

        logout() {
            const date = new Date();

            // Set it expire in -1 days
            date.setTime(date.getTime() + (-1 * 24 * 60 * 60 * 1000));

            document.cookie = cookieName+"=; expires="+date.toUTCString()+"; path=/";
            window.location.href = import.meta.env.VITE_LOGOUT_URL;
        },
        editRole(): 'full' | 'restricted' | 'view' {
            const character = useAppDataStore().character;
            if (character?.metadata?.owner === true) return 'full';
            if (character?.permissions?.edit === true) return 'restricted';
            return 'view';
        },
        canRestrictedEdit(): boolean {
            const role = this.editRole();
            return role === 'restricted'
                || (role === 'full' && useAppDataStore().props.edit === true);
        },
        canEditAdvances(): boolean {
            return this.canRestrictedEdit();
        },
        canEditEquipStatus(): boolean {
            return this.canRestrictedEdit();
        },
        isWorldOwner(): boolean {
            const world = useAppDataStore().world;
            return !!this.user?.uuid
                && !!world?.owner?.uuid
                && this.user.uuid === world.owner.uuid;
        },
    }
})
